import { createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from '../../../redux/store';
import { ChecklistQuestionCategory, RolesType } from '@rapid-recovery-agency-inc/insightt-graphql';
import { generateUniqueId } from '../utils';

export interface ChecklistItemsState {
  id: string;
  text: string;
  input: boolean;
  photo: boolean;
}

export interface ShiftChecklistResponse {
  question: string;
  file?: string;
  photo?: string;
  comment?: string;
  response: string;
  photoFile?: File | null;
  photoUrl?: string;
  id: number;
}

export interface ChecklistState {
  errorStepOneActivityType: boolean;
  errorStepOneUserRole: boolean;
  errorStepTwoEmptyQuestion: boolean;
  selectedCategory: ChecklistQuestionCategory[];
  selectedRole: RolesType[];
  checklistItems: ChecklistItemsState[];
  submitChecklistResponses: ShiftChecklistResponse[];
}

const initialState: ChecklistState = {
  errorStepOneActivityType: false,
  errorStepOneUserRole: false,
  errorStepTwoEmptyQuestion: false,
  selectedCategory: [],
  selectedRole: [],
  checklistItems: [{ id: generateUniqueId(), text: '', input: false, photo: false }],
  submitChecklistResponses: [],
};

export const checklistSlice = createSlice({
  name: 'checklist',
  initialState,
  reducers: {
    setErrorStepOneActivityType: (state, action) => {
      state.errorStepOneActivityType = action.payload;
    },
    setErrorStepOneUserRole: (state, action) => {
      state.errorStepOneUserRole = action.payload;
    },
    setErrorStepTwoEmptyQuestion: (state, action) => {
      state.errorStepTwoEmptyQuestion = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSelectedRole: (state, action) => {
      state.selectedRole = action.payload;
    },
    addChecklistItem: (state) => {
      state.checklistItems.push({
        id: generateUniqueId(),
        text: '',
        input: false,
        photo: false,
      });
    },
    removeChecklistItem: (state, action) => {
      state.checklistItems = state.checklistItems.filter((item) => item.id !== action.payload);
    },
    updateChecklistItem: (state, action) => {
      const { id, updatedItem } = action.payload;
      const index = state.checklistItems.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.checklistItems[index] = updatedItem;
      }
    },
    setSubmitChecklistResponses: (state, action) => {
      state.submitChecklistResponses = action.payload;
    },
    resetChecklist: () => {
      return { ...initialState };
    },
  },
});

export const {
  setErrorStepOneActivityType,
  setErrorStepOneUserRole,
  setErrorStepTwoEmptyQuestion,
  setSelectedCategory,
  setSelectedRole,
  addChecklistItem,
  removeChecklistItem,
  updateChecklistItem,
  resetChecklist,
  setSubmitChecklistResponses,
} = checklistSlice.actions;

export const { reducer: checklistReducer } = checklistSlice;

// Selectors
const getChecklistSlice = (state: AppState) => state.checklistReducer;
export const getChecklist = createSelector(getChecklistSlice, (state) => state);
